import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router, Scroll } from '@angular/router';
import { takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/shared/components/base-component/base.component';
import { ROUTER_PATHS } from 'src/app/shared/constants';
import { NotificationSoundService } from 'src/app/shared/services';
import { LocalStorageService, StorageItem } from 'src/app/shared/services/local-storage.service';
import { SignalRService } from 'src/app/shared/services/signalr.service';
import { ToggleDirections } from '../header/header.model';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() currentActiveParentRoute!: string;
  @Input() isMobileDevice!: boolean;
  @Input() isExpanded!: boolean;
  @Input() menuItems: any;

  toggleDirections = ToggleDirections.LEFT;
  unreadMessageCount = 0;
  isTabActive = true;

  @Output() setIsExpanded = new EventEmitter<boolean>();

  constructor(
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly signalRService: SignalRService,
    private readonly notificationSoundService: NotificationSoundService,
    private readonly localStorageService: LocalStorageService
  ) {
    super();
    this.setActiveRoute();
    this.getUnreadMessageCount();
    this.playMessageSound();
  }

  ngOnInit(): void {
    this.isExpanded = !this.isMobileDevice;
    this.currentUser = this.localStorageService.getItem(StorageItem.CurrentUser);
    this.setIsExpanded.emit(this.isExpanded);
    window.addEventListener('visibilitychange', () => {
      this.isTabActive = document.visibilityState === 'visible';
    });
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd && this.isMobileDevice) {
        this.isExpanded = false;
        this.setIsExpanded.emit(false);
        this.cdr.detectChanges();
      }
    });
  }

  ngOnChanges(): void {
    this.cdr.detectChanges();
  }

  setActiveRoute(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentActiveParentRoute = event.url.split('/')[1];
      } else if (event instanceof Scroll) {
        this.currentActiveParentRoute = event.routerEvent.url.split('/')[1];
      }
      this.cdr.detectChanges();
    });
  }

  getUnreadMessageCount(): void {
    this.signalRService.unreadMessageCount$.pipe(takeUntil(this.destroy$)).subscribe((count) => {
      this.unreadMessageCount = count;
      this.cdr.markForCheck();
    });
  }

  playMessageSound(): void {
    this.signalRService.chatHistory.pipe(takeUntil(this.destroy$)).subscribe((chat) => {
      if ((chat && this.currentActiveParentRoute !== ROUTER_PATHS.message) || (!this.isTabActive && chat)) {
        this.notificationSoundService.playSound();
        this.cdr.detectChanges();
      }
    });
  }

  handleSidebarToggle() {
    this.setIsExpanded.emit(!this.isExpanded);
    this.isExpanded = !this.isExpanded;
  }

  toggleMenu(menu: any): void {
    menu.isOpen = !menu.isOpen;
    this.cdr.detectChanges();
  }

  getIsParentActive(menuItem: any, currentActiveParentRoute: string): boolean {
    return menuItem?.moduleKeys.includes(currentActiveParentRoute);
  }
}
